/**
 * Users and Specialists-related GraphQL queries
 * User management, enrollment specialists, care specialists, patient enrollers
 */

export const USERS_QUERIES = {
  // User Data query
  GET_HBOX_USERS: `
    query GetHBoxUsers {
      api_hboxuser(where: {is_active: {_eq: true}}) {
        id
        first_name
        last_name
        type
      }
    }
  `,

  // User Data by IDs
  GET_HBOX_USERS_BY_IDS: `
    query GetHBoxUsersByIds($ids: [Int!]) {
      api_hboxuser(where: {id: {_in: $ids}}) {
        id
        first_name
        last_name
        type
      }
    }
  `,

  // All Users Query (from GRAPHQL_QUERIES)
  ALL_USERS: `
    query MyQuery {
      api_hboxuser(where: {type: {_in: [EA, ES, CA, CS, PA, PE]}}) {
        id
        first_name
        last_name
        type
      }
    }
  `,

  // PA Role Queries
  GET_PATIENT_ENROLLERS: `
    query GetPatientEnrollers {
      pep_vw_patient_enroller_list {
        patient_enroller_id
        first_name
        last_name
        full_name
        email
        clinic_names
        clinic_ids
        deferred_task
        follow_up
      }
    }
  `,

  // Enrollment Specialists query
  GET_ENROLLMENT_SPECIALISTS: `
    query GetEnrollmentSpecialists {
      pep_vw_enrollment_specialists(order_by: {hbox_user_id: desc}) {
        hbox_user_id
        es_name
      }
    }
  `,

  // Care Specialist queries
  GET_CARE_SPECIALISTS: `
    query GetCareSpecialists {
      pep_vw_care_specialist_list(order_by: {care_specialist_id: desc}) {
        care_specialist_id
        first_name
        last_name
        full_name
        email
        clinic_names
        clinic_ids
        deferred_task
        follow_up
      }
    }
  `,

  GET_CARE_SPECIALIST_STATUS: `
    query GetCareSpecialistStatus($careSpecialistId: Int!) {
      pep_vw_care_specialist_list(where: {care_specialist_id: {_eq: $careSpecialistId}}) {
        deferred_task
        follow_up
        care_specialist_id
      }
    }
  `,

  GET_CARE_SPECIALIST_ASSIGNMENTS: `
    query GetCareSpecialistAssignments($careSpecialistId: Int!) {
      pep_care_specialist_active_assignments(where: {care_specialist_id: {_eq: $careSpecialistId}}) {
        clinic_ids
      }
    }
  `,

  // Patient Enroller Clinic IDs query
  GET_PE_CLINIC_IDS: `
    query GetPEClinicIds($patientEnrollerId: Int!) {
      pep_patient_enroller_active_assignments(where: {patient_enroller_id: {_eq: $patientEnrollerId}}) {
        clinic_ids
      }
    }
  `,

  // Patient Enroller Status query
  GET_PE_STATUS: `
    query GetPEStatus($patientEnrollerId: Int!) {
      pep_vw_patient_enroller_list(where: {patient_enroller_id: {_eq: $patientEnrollerId}}) {
        deferred_task
        follow_up
        patient_enroller_id
      }
    }
  `,

  // VPE Management queries
  GET_ENROLLMENT_SPECIALISTS_LIST: `
    query MyQuery {
      pep_vw_enrollment_specialists(order_by: {hbox_user_id: desc}) {
        hbox_user_id
        first_name
        last_name
        es_name
        email
        deferred_task
      }
    }
  `,

  // VPE Management queries
  GET_VPES: `
    query MyQuery {
      pep_vw_enrollment_specialists {
        hbox_user_id
        es_name
        deferred_task
      }
    }
  `,

  // From GRAPHQL_QUERIES - Dropdown VPE specialist
  DROPDOWN_VPE_SPECIALISTS: `
    query MyQuery {
      pep_vw_enrollment_specialists {
        hbox_user_id
        es_name
      }
    }
  `,

  // User Details Audit Log
  GET_USER_DETAILS_AUDIT_LOG: `
    query GetUserDetailsAuditLog($userId: Int!) {
      pep_user_details_audit_log(
        where: { target_user_id: { _eq: $userId } }
        order_by: { changed_at: desc }
      ) {
        metadata
        changed_by
        changed_at
        action
      }
      pep_user_details_audit_log_aggregate(
        where: { target_user_id: { _eq: $userId } }
      ) {
        aggregate {
          count
        }
      }
    }
  `,

  // ES list (from GRAPHQL_QUERIES)
  ES_LIST: `
    query MyQuery {
      pep_vw_enrollment_specialists {
        hbox_user_id
        es_name
      }
    }
  `,

  // ES Deferred Task Permission (from GRAPHQL_QUERIES)
  ES_DEFERRED_TASK_PERMISSION: `
    query MyQuery($userId: Int!) {
      pep_vw_enrollment_specialists(where: {hbox_user_id: {_eq: $userId}}) {
        deferred_task
        hbox_user_id
      }
    }
  `,

  // ES Deferred Patient List (from GRAPHQL_QUERIES)
  ES_DEFERRED_PATIENT_LIST: `
    query MyQuery {
      pep_vw_vpe_deferred_list(limit: 1) {
        pep_patient_clinic_data_id
      }
    }
  `,

  // Users Dashboard queries
  GET_USERS_DASHBOARD: `
    query GetUsersDashboard($limit: Int!, $offset: Int!, $orderBy: [pep_vw_user_dashboard_order_by!]) {
      pep_vw_user_dashboard(limit: $limit, offset: $offset, order_by: $orderBy) {
        id
        user_name
        email
        user_type
        phone_number
        updated
        last_active
      }
      pep_vw_user_dashboard_aggregate {
        aggregate {
          count
        }
      }
    }
  `,

  SEARCH_USERS_DASHBOARD: `
    query SearchUsersDashboard($limit: Int!, $offset: Int!, $where: pep_vw_user_dashboard_bool_exp!, $orderBy: [pep_vw_user_dashboard_order_by!]) {
      pep_vw_user_dashboard(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
        id
        user_name
        email
        user_type
        phone_number
        updated
        last_active
      }
      pep_vw_user_dashboard_aggregate(where: $where) {
        aggregate {
          count
        }
      }
    }
  `,

  GET_USER_BY_ID: `
    query GetUserById($id: Int!) {
      pep_vw_user_dashboard(where: {id: {_eq: $id}}) {
        id
        user_name
        email
        user_type
        phone_number
        updated
        last_active
      }
    }
  `,

  // User Groups Dashboard queries
  GET_USER_GROUPS_DASHBOARD: `
    query GetUserGroupsDashboard($limit: Int!, $offset: Int!, $orderBy: [pep_vw_user_group_dashboard_order_by!]) {
      pep_vw_user_group_dashboard(limit: $limit, offset: $offset, order_by: $orderBy) {
        user_group_id
        user_group_name
        user_count
        created_datetime
        updated_datetime
      }
      pep_vw_user_group_dashboard_aggregate {
        aggregate {
          count
        }
      }
    }
  `,

  SEARCH_USER_GROUPS_DASHBOARD: `
    query SearchUserGroupsDashboard($limit: Int!, $offset: Int!, $where: pep_vw_user_group_dashboard_bool_exp!, $orderBy: [pep_vw_user_group_dashboard_order_by!]) {
      pep_vw_user_group_dashboard(limit: $limit, offset: $offset, where: $where, order_by: $orderBy) {
        user_group_id
        user_group_name
        user_count
        created_datetime
        updated_datetime
      }
      pep_vw_user_group_dashboard_aggregate(where: $where) {
        aggregate {
          count
        }
      }
    }
  `,

  GET_ALL_USER_GROUPS_FOR_DROPDOWN: `
    query GetAllUserGroupsForDropdown {
      pep_vw_user_group_dashboard {
        user_group_id
        user_group_name
        user_count
        created_datetime
        updated_datetime
      }
    }
  `,

  GET_USER_GROUP_BY_ID: `
    query GetUserGroupById($userGroupId: Int!) {
      pep_vw_user_group_dashboard(where: {user_group_id: {_eq: $userGroupId}}) {
        user_group_id
        user_group_name
        user_count
        user_ids
        created_datetime
        updated_datetime
      }
    }
  `,

  GET_USERS_BY_IDS: `
    query GetUsersByIds($userIds: [Int!]!) {
      pep_vw_user_dashboard(where: {id: {_in: $userIds}}) {
        id
        user_name
        email
        user_type
        phone_number
        updated
        last_active
      }
    }
  `,

  // Reference User Roles query
  GET_USER_ROLES: `
    query GetUserRoles {
      reference_user_role {
        role
        role_id
      }
    }
  `,

  // User Group Audit Log query
  GET_USER_GROUP_AUDIT_LOG: `
    query GetUserGroupAuditLog($userGroupId: Int!) {
      pep_user_group_audit_log(where: {user_group_id: {_eq: $userGroupId}}, order_by: {action_datetime: desc}) {
        pep_user_group_audit_log_id
        user_group_id
        action_type
        action_datetime
        action_by
        user_data
        user_group_data
        notes
        metadata
      }
      pep_user_group_audit_log_aggregate(where: {user_group_id: {_eq: $userGroupId}}) {
        aggregate {
          count
        }
      }
    }
  `,

  // User Resource Availability query
  GET_REF_RESOURCE_AVAILABILITY: `
    query GetRefResourceAvailability($userId: Int!) {
      ref_resource_availability(where: {is_deleted: {_eq: false}, user_id: {_eq: $userId}}) {
        is_active
        is_deleted
        available_from_date
        available_to_date
        created_by
        resource_availability_id
        updated_by
        user_id
        user_timezone
        created_datetime
        updated_datetime
        resourse_availbility(where: {is_deleted: {_eq: false}}) {
          available_end_time
          available_start_time
          day_of_week
          resource_availability_id
          is_deleted
        }
      }
    }
  `
};